// ─────────────────────────────────────────────────────────────────────────────
// WP-15 REQ-130 — Sección "Moderación" del Admin. Paridad 1:1 con el maquetado
// (`fundamentales-admin.js` → `bodies.moderacion`, `_modReportRow`, `_renderModWords`).
//
// UNA sola página apilada, como el prototipo — ya no hay sub-pestañas:
//   1. Palabras Bloqueadas → tarjeta resumen (contador + idiomas) con botón "Gestionar →".
//   2. Configuración de Auto-Moderación → 3 controles + Guardar.
//   3. Bandeja de Reportes → barra de 6 filtros, tabla de 8 columnas y pie "Mostrando X–Y de N".
// "Gestionar →" abre una SUBVISTA (no un tab) con el catálogo completo: pestañas por idioma con
// conteo, buscador, filtro de región, edición en línea y paginación.
//
// Origen de los datos (RecognitionService, absorbido en SurveyService):
//   - `useModerationReports` trae el reporte Y el contexto del reconocimiento (receptores, resumen
//     del mensaje, tipo y OU/BU/Site), que el backend proyecta desde `Recognition` + sus snapshots.
//     La bandeja se pinta y se filtra con UNA respuesta: ya no hay un `GET v1/recognitions/{id}`
//     por fila. El modal pide el detalle del REPORTE (`v1/moderation/reports/{id}`) solo al abrirlo,
//     porque ahí hace falta el texto íntegro y no el resumen.
//   - "Bloqueo automático por IA" (REQ-055) YA está cableado: al enviar un reconocimiento, el backend
//     pasa el mensaje por Azure AI Content Safety si este toggle está activo (aditivo al catálogo de
//     palabras). El umbral de severidad se configura en el servidor (ContentSafety:SeverityThreshold).
// Estados honestos (loading/error/empty). CERO mocks.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useModerationReports,
  useModerationReportDetail,
  useHideRecognition,
  useRestoreRecognition,
  useReopenReport,
  useBlockedWords,
  useCreateBlockedWord,
  useUpdateBlockedWord,
  useDeleteBlockedWord,
  useModerationSettings,
  useSaveModerationSettings,
  useCountryLanguages,
  useSaveCountryLanguages,
  useNotifyModerationAdmin,
  useModerationNotifyPreview,
  getRecognitionErrorMessage,
  getRecognitionErrorReason,
} from '@/api/recognition';
import type { TFunction } from 'i18next';
import type {
  ModerationSettingsDto,
  ModerationStatus,
  ReportDto,
  ReportDetailDto,
  ReportRecoType,
  BlockedWordDto,
  CountryLanguageMapDto,
} from '@/api/recognition';
import { useOUItems } from '@/hooks/useOUs';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import { SUPPORTED_LANGUAGE_CODES } from '@/i18n/languages';
import { Modal, ModalHeader, ModalBody } from '@/components/common';
import { FloatingTooltip } from '@/components/common/FloatingTooltip';
import { StateBlock } from '../components';
import { AdminSection, AdminSaveBar, AdminPagerFooter } from './adminShared';

/**
 * Ícono de ayuda flotante junto a un control de moderación — mismo patrón visual que `ToggleHelp`
 * de `adminShared` (FloatingTooltip + botón `fa-circle-info`), pero con claves del namespace de
 * moderación. Existe para explicar la DEPENDENCIA entre los interruptores y el catálogo de palabras
 * bloqueadas: el apagador global manda sobre todo, y la IA es aditiva al catálogo. `k` indexa
 * `admin.moderation.<k>` (namespace `recognition`).
 */
function ModerationHelp({ k }: Readonly<{ k: string }>) {
  const { t } = useTranslation('recognition');
  return (
    <FloatingTooltip tooltipClassName="adm-vista-tooltip" content={t(`admin.moderation.${k}`)}>
      {(anchorProps) => (
        <button type="button" className="adm-vista-info"
          aria-label={t('admin.moderation.helpAria')} {...anchorProps}>
          <i className="fas fa-circle-info" aria-hidden="true" />
        </button>
      )}
    </FloatingTooltip>
  );
}

// ─── Taxonomía ISO (RF-082) ────────────────────────────────────────────────────
//
// Antes esta pantalla llevaba dos arreglos hardcodeados con la taxonomía del maquetado
// (`ES-LATAM`/`EN-UK` como idiomas y `España`/`México`/`Europa` como "países"). La spec exige
// códigos ISO y una TABLA país→idiomas configurable, así que los ámbitos de país salen del servidor:
// de los países y regiones de la tabla, más los que ya usan las palabras capturadas.
//
// Los IDIOMAS, en cambio, ya NO salen de la tabla. Salían, y ese era el defecto que reportó QA: la
// semilla declaraba 23 etiquetas distintas (`es-MX`, `es-AR`, `es-CL`, `it-IT`…) y cada una se
// pintaba como una pestaña, en una plataforma que soporta 7 idiomas. Ahora la lista de idiomas es
// `SUPPORTED_LANGUAGE_CODES` —la MISMA fuente que el selector de idioma de la app—, de modo que no
// pueden volver a divergir. Decisión del cliente (2026-08-05, lectura ESTRICTA del defecto): solo
// los 7 códigos base, sin variante de país; la granularidad por país que pide RF-082 se expresa con
// el campo `country` de la palabra, que el backend filtra por una vía independiente de la del idioma.

/** Comodín "todos los países" tal y como lo persiste el backend. */
const ANY_COUNTRY = '*';

/** Pestaña donde caen las palabras legacy cuyo idioma no es ninguno de los 7 (DP-3: no se migran). */
const OTHER_LANG = '__other';

/** Idioma base de una etiqueta BCP-47: `es-MX` → `es`. Espejo de `ModerationLocales.BaseLanguage`. */
function baseLanguage(tag: string): string {
  return tag.split(/[-_]/)[0].toLowerCase();
}

function isSupportedLanguage(tag: string): boolean {
  return SUPPORTED_LANGUAGE_CODES.includes(baseLanguage(tag));
}

/**
 * Idiomas del gestor: los 7 de la plataforma, SIEMPRE los 7 y en el orden canónico —igual que el
 * maquetado, que pinta su tira de pestañas completa aunque alguna vaya a cero—, más una pestaña
 * "otros" que aparece solo si el catálogo trae etiquetas fuera de esos 7.
 *
 * Ese "otros" no es decorativo: DP-3 dice que no se migra nada, así que en Mongo pueden seguir
 * palabras guardadas como `it-IT` o `ES-LATAM`. Esconderlas las dejaría aplicando en el filtro pero
 * invisibles para quien administra el catálogo, que es la peor de las combinaciones.
 */
function languageTabs(words: BlockedWordDto[]): string[] {
  const hasOther = words.some((w) => w.languageCode
    && w.languageCode !== ANY_COUNTRY && !isSupportedLanguage(w.languageCode));
  return hasOther ? [...SUPPORTED_LANGUAGE_CODES, OTHER_LANG] : [...SUPPORTED_LANGUAGE_CODES];
}

/** ¿Cae esta palabra en la pestaña indicada? Las variantes legacy caen en su idioma base. */
function inLanguageTab(word: BlockedWordDto, tab: string): boolean {
  const code = word.languageCode;
  if (!code || code === ANY_COUNTRY) return false;
  if (tab === OTHER_LANG) return !isSupportedLanguage(code);
  return baseLanguage(code) === tab;
}

/**
 * Ámbitos de país ofrecidos: los países ISO de la tabla, sus regiones (`@LATAM`) y los que ya
 * aparecen en el catálogo. El comodín se ofrece aparte, siempre primero, porque no es un país.
 */
function countryOptions(map: CountryLanguageMapDto | undefined, words: BlockedWordDto[]): string[] {
  const set = new Set<string>();
  Object.keys(map?.countries ?? {}).forEach((c) => set.add(c));
  Object.keys(map?.regions ?? {}).forEach((r) => set.add(r));
  words.forEach((w) => { if (w.country && w.country !== ANY_COUNTRY) set.add(w.country); });
  return [...set].sort((a, b) => a.localeCompare(b));
}

/**
 * RF-082 — validación de los códigos ANTES de guardar. El backend normaliza y rechaza, pero un
 * `PUT` que falla entero por una fila mal tecleada, o que guarda `FRANCIA` convertido en otra cosa,
 * es peor experiencia que decirlo en el formulario.
 */
function isIsoCountry(code: string): boolean {
  return /^[A-Za-z]{2}$/.test(code.trim());
}

/**
 * RF-082 — no basta con que tenga forma BCP-47: el backend rechaza cualquier idioma que no sea uno
 * de los 7 de la plataforma, y sin esta comprobación el `PUT` fallaba entero por una fila tecleada
 * con una variante (`es-MX`) que parecía perfectamente válida.
 */
function isLanguageTagOrEmpty(tag: string): boolean {
  const trimmed = tag.trim();
  return trimmed === '' || SUPPORTED_LANGUAGE_CODES.includes(trimmed);
}

/** Estados del maquetado ← enum `ModerationStatus` del backend. */
type UiStatus = 'pendiente' | 'restaurado' | 'eliminado';
const UI_TO_BACKEND: Record<UiStatus, ModerationStatus> = {
  pendiente: 'Pendiente',
  restaurado: 'Desestimado',
  eliminado: 'Resuelto',
};
function toUiStatus(estado: ModerationStatus): UiStatus | null {
  if (estado === 'Pendiente') return 'pendiente';
  if (estado === 'Desestimado') return 'restaurado';
  if (estado === 'Resuelto') return 'eliminado';
  return null; // Publicado — no debería aparecer en la bandeja.
}

/** Id del título del modal de escalado (accesibilidad: `aria-labelledby`). */
const ESCALATE_TITLE_ID = 'moderation-escalate-title';

/** Nombres de los receptores del reconocimiento reportado (columna "Para"). */
function recipientNames(report: Pick<ReportDto, 'receptores'>): string {
  return (report.receptores ?? []).map((r) => r.nombre).join(', ');
}

// ─── Pantalla ──────────────────────────────────────────────────────────────────

export function AdminModeration() {
  const { t } = useTranslation('recognition');
  const [managingWords, setManagingWords] = useState(false);

  return (
    <AdminSection title={t('admin.moderation.contentTitle')}>
      {managingWords ? (
        <BlockedWordsManager onBack={() => setManagingWords(false)} />
      ) : (
        <>
          <BlockedWordsSummary onManage={() => setManagingWords(true)} />
          <AutoModerationSettings />
          <ReportsInbox />
        </>
      )}
    </AdminSection>
  );
}

// ─── 1. Palabras bloqueadas — tarjeta resumen ──────────────────────────────────

function BlockedWordsSummary({ onManage }: Readonly<{ onManage: () => void }>) {
  const { t } = useTranslation('recognition');
  const words = useBlockedWords();
  const list = words.data ?? [];

  // RF-082 — idiomas realmente presentes, agrupados por idioma BASE y en el orden canónico de la
  // plataforma. Antes listaba la etiqueta cruda de cada palabra, así que un catálogo con variantes
  // por país enseñaba aquí la misma veintena de códigos que QA reportó en las pestañas. Las
  // etiquetas legacy fuera de los 7 se siguen listando —existen, y ocultarlas sería mentir—, pero
  // también reducidas a su base, así que el total no puede dispararse.
  const present = new Set(list
    .map((w) => w.languageCode)
    .filter((l) => l && l !== ANY_COUNTRY)
    .map(baseLanguage));
  const langs = [
    ...SUPPORTED_LANGUAGE_CODES.filter((l) => present.has(l)),
    ...[...present].filter((l) => !SUPPORTED_LANGUAGE_CODES.includes(l)).sort((a, b) => a.localeCompare(b)),
  ].map((l) => l.toUpperCase());

  return (
    <>
      <h4 className="adm-sub-title">{t('admin.moderation.wordsTitle')} <ModerationHelp k="wordsHelp" /></h4>
      <p className="adm-block-desc">{t('admin.moderation.wordsDesc')}</p>

      <StateBlock
        isLoading={words.isLoading}
        isError={words.isError}
        error={words.error}
        isEmpty={false}
        onRetry={() => { words.refetch(); }}
      >
        <div className="adm-words-summary">
          <div>
            <span className="adm-words-count">{list.length}</span>
            <span className="adm-words-count-lbl">{t('admin.moderation.wordsCounted')}</span>
            <div className="adm-words-langs">{langs.length ? langs.join(' · ') : '—'}</div>
          </div>
          <button type="button" className="btn btn--primary" onClick={onManage}>
            {t('admin.moderation.manageWords')} <i className="fas fa-arrow-right" />
          </button>
        </div>
      </StateBlock>
    </>
  );
}

// ─── 2. Configuración de auto-moderación ───────────────────────────────────────

/** Rango que valida el backend para el umbral y para el tope diario de reportes. */
const NUM_MIN = 1;
const NUM_MAX = 100;

/**
 * Campo numérico acotado. Conserva el texto CRUDO mientras se edita y solo acota al salir: con un
 * `value` controlado y clamp en cada tecla, borrar el contenido lo repone al mínimo y el siguiente
 * dígito queda concatenado (borrar "1" y teclear "3" daba 13).
 */
function BoundedNumberInput({ value, label, title, disabled, onCommit }: Readonly<{
  value: number;
  label: string;
  title?: string;
  disabled?: boolean;
  onCommit: (n: number) => void;
}>) {
  const [raw, setRaw] = useState<string | null>(null);
  const shown = raw ?? String(value);

  return (
    <input type="number" className="adm-num-input" min={NUM_MIN} max={NUM_MAX}
      value={shown} disabled={disabled} aria-label={label} title={title}
      onChange={(e) => {
        setRaw(e.target.value);
        const n = Number(e.target.value);
        if (e.target.value !== '' && Number.isFinite(n) && n >= NUM_MIN && n <= NUM_MAX) onCommit(n);
      }}
      onBlur={() => {
        const n = Number(shown);
        onCommit(shown === '' || !Number.isFinite(n) ? NUM_MIN : Math.min(NUM_MAX, Math.max(NUM_MIN, n)));
        setRaw(null);
      }} />
  );
}

function AutoModerationSettings() {
  const { t } = useTranslation('recognition');
  const query = useModerationSettings();
  const save = useSaveModerationSettings();

  const [draft, setDraft] = useState<ModerationSettingsDto | null>(null);
  const [dirty, setDirty] = useState(false);
  useUnsavedChangesGuard(dirty);
  const loaded = query.data;

  // Sincroniza el borrador cuando llega la config (y no se pisa lo que el usuario está editando).
  if (loaded && draft === null) setDraft(loaded);

  const patch = (p: Partial<ModerationSettingsDto>) => {
    setDraft((d) => (d ? { ...d, ...p } : d));
    setDirty(true);
  };

  // El bloque entero depende de una sola consulta: si no cargó, los controles no se pueden operar y
  // hay que decir por qué. Antes el estado vivía dentro del <details>, donde no se veía.
  if (query.isLoading || query.isError) {
    return (
      <>
        <h4 className="adm-sub-title">{t('admin.moderation.autoTitle')}</h4>
        <StateBlock
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          isEmpty={false}
          onRetry={() => { query.refetch(); }}
        >
          {null}
        </StateBlock>
      </>
    );
  }

  return (
    <>
      <h4 className="adm-sub-title">{t('admin.moderation.autoTitle')}</h4>

      <div className="adm-config-row">
        {/* REQ-055 — bloqueo automático con IA (Azure AI Content Safety). Con el toggle activo, cada
            mensaje pasa por la IA al enviarse; aditivo al catálogo de palabras. */}
        <div className="adm-config-item">
          <span>{t('admin.moderation.aiAutoBlock')} <ModerationHelp k="aiHelp" /></span>
          <button type="button" role="switch" aria-checked={draft?.autoAiBlocking ?? false}
            disabled={!draft}
            aria-label={t('admin.moderation.aiAutoBlock')}
            title={t('admin.moderation.aiAutoBlockHint')}
            className={`adm-toggle ${draft?.autoAiBlocking ? 'adm-toggle--on' : ''}`}
            onClick={() => draft && patch({ autoAiBlocking: !draft.autoAiBlocking })}>
            <span className="adm-toggle-knob" />
          </button>
        </div>
        {/* El maquetado muestra 3; el campo nace en 1, que es la regla vigente (DP-1). Subirlo es
            una decisión explícita del administrador, no un cambio silencioso de comportamiento. */}
        <div className="adm-config-item">
          <span>{t('admin.moderation.hideThreshold')}</span>
          <BoundedNumberInput value={draft?.hideThreshold ?? 1} disabled={!draft}
            label={t('admin.moderation.hideThreshold')}
            title={t('admin.moderation.hideThresholdHint')}
            onCommit={(n) => patch({ hideThreshold: n })} />
        </div>
        <div className="adm-config-item">
          <span>{t('admin.moderation.notifyReporter')}</span>
          <button type="button" role="switch" aria-checked={draft?.notifyReporter ?? false}
            disabled={!draft}
            aria-label={t('admin.moderation.notifyReporter')}
            title={t('admin.moderation.notifyReporterHint')}
            className={`adm-toggle ${draft?.notifyReporter ? 'adm-toggle--on' : ''}`}
            onClick={() => draft && patch({ notifyReporter: !draft.notifyReporter })}>
            <span className="adm-toggle-knob" />
          </button>
        </div>
      </div>

      <p className="adm-note adm-note--info">
        <i className="fas fa-circle-info" /> {t('admin.moderation.aiPendingNote')}
      </p>

      {/* Los tres toggles que el backend SÍ persiste hoy. El maquetado no los muestra en esta
          pantalla, así que van plegados: alimentan ReportRecognitionCommandHandler (apagado global,
          default de "reportar" y tope diario) y retirarlos sería perder su administración. */}
      <details className="adm-advanced">
        <summary>{t('admin.moderation.advanced')}</summary>
        {draft && (
          <div className="adm-config-row">
            <div className="adm-config-item">
              <span>{t('admin.moderation.globalEnabled')} <ModerationHelp k="globalHelp" /></span>
              <button type="button" role="switch" aria-checked={draft.globalEnabled}
                aria-label={t('admin.moderation.globalEnabled')}
                className={`adm-toggle ${draft.globalEnabled ? 'adm-toggle--on' : ''}`}
                onClick={() => patch({ globalEnabled: !draft.globalEnabled })}>
                <span className="adm-toggle-knob" />
              </button>
            </div>
            <div className="adm-config-item">
              <span>{t('admin.moderation.reportEnabledGlobal')}</span>
              <button type="button" role="switch" aria-checked={draft.reportEnabledGlobal}
                aria-label={t('admin.moderation.reportEnabledGlobal')}
                className={`adm-toggle ${draft.reportEnabledGlobal ? 'adm-toggle--on' : ''}`}
                onClick={() => patch({ reportEnabledGlobal: !draft.reportEnabledGlobal })}>
                <span className="adm-toggle-knob" />
              </button>
            </div>
            <div className="adm-config-item">
              <span>{t('admin.moderation.dailyReportLimit')}</span>
              <BoundedNumberInput value={draft.dailyReportLimit}
                label={t('admin.moderation.dailyReportLimit')}
                onCommit={(n) => patch({ dailyReportLimit: n })} />
            </div>
            <ReportOverridesEditor
              overrides={draft.reportEnabledByOu ?? null}
              onChange={(reportEnabledByOu) => patch({ reportEnabledByOu })} />
          </div>
        )}
      </details>

      {save.isError && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(save.error, t('admin.saveError'))}
        </p>
      )}

      <div className="adm-save-row">
        <AdminSaveBar
          onSave={() => draft && save.mutate(draft, { onSuccess: () => setDirty(false) })}
          saving={save.isPending}
          dirty={dirty}
          saved={save.isSuccess}
        />
      </div>
    </>
  );
}

/**
 * RF-084 / RN-M03 — overrides de "permitir reportes" por NODO (OU, BU o Site).
 *
 * El campo ya existía en el contrato pero ninguna pantalla lo editaba, así que apagar los reportes en
 * una geografía concreta solo era posible tocando Mongo a mano. El backend resuelve el valor efectivo
 * en cascada Site → BU → OU → ancestros → global y se para en el primero que exista; aquí solo se
 * declaran los nodos que se desvían del default global.
 *
 * El nodo se elige de la lista de OUs, pero también se admite teclear un id: BU y Site vienen del
 * registro Frontline y no siempre son OUs del árbol.
 */
function ReportOverridesEditor({ overrides, onChange }: Readonly<{
  overrides: Record<string, boolean> | null;
  onChange: (next: Record<string, boolean> | null) => void;
}>) {
  const { t } = useTranslation('recognition');
  const { data: ouItems = [] } = useOUItems();
  const [node, setNode] = useState('');

  const entries = Object.entries(overrides ?? {});
  const nameOf = (id: string) => ouItems.find((o) => o.id === id)?.name ?? id;

  /**
   * RF-084 — el backend resuelve el nodo SIN distinguir mayúsculas, así que "Mexico" y "mexico" son
   * el mismo nodo. En JS las claves de objeto sí distinguen, de modo que teclear las dos creaba una
   * configuración ambigua que el servidor ahora rechaza al guardar. Se detecta aquí para avisar en el
   * momento —con el id ya existente a la vista— en lugar de fallar al pulsar Guardar.
   */
  const collision = (() => {
    const id = node.trim();
    if (!id) return null;
    return Object.keys(overrides ?? {}).find(
      (k) => k.toLocaleLowerCase() === id.toLocaleLowerCase() && k !== id,
    ) ?? null;
  })();

  const add = (enabled: boolean) => {
    const id = node.trim();
    if (!id || collision) return;
    onChange({ ...(overrides ?? {}), [id]: enabled });
    setNode('');
  };

  const remove = (id: string) => {
    const next = { ...(overrides ?? {}) };
    delete next[id];
    // Sin overrides se manda null y no un objeto vacío: es lo que el backend entiende por
    // "esta configuración no declara excepciones".
    onChange(Object.keys(next).length > 0 ? next : null);
  };

  return (
    <div className="adm-config-item adm-config-item--block">
      <span>{t('admin.moderation.overridesTitle')}</span>
      <p className="adm-block-desc">{t('admin.moderation.overridesDesc')}</p>

      <div className="adm-words-form">
        <label className="adm-words-field adm-words-field--grow">
          <span>{t('admin.moderation.overridesNode')}</span>
          {/* aria-label explícita: el <datalist> cuelga del mismo <label>, así que sus opciones se
              cuelan en el textContent de la etiqueta y el campo se queda sin nombre accesible
              limpio ("Unidad, BU o SiteLATAM…"). */}
          <input type="text" list="moderation-override-nodes" value={node}
            aria-label={t('admin.moderation.overridesNode')}
            placeholder={t('admin.moderation.overridesNodePlaceholder')}
            onChange={(e) => setNode(e.target.value)} />
          <datalist id="moderation-override-nodes">
            {ouItems.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </datalist>
        </label>
        <button type="button" className="btn btn--primary" disabled={!node.trim() || Boolean(collision)}
          onClick={() => add(true)}>
          <i className="fas fa-check" /> {t('admin.moderation.overridesAllow')}
        </button>
        <button type="button" className="btn btn--primary" disabled={!node.trim() || Boolean(collision)}
          onClick={() => add(false)}>
          <i className="fas fa-ban" /> {t('admin.moderation.overridesBlock')}
        </button>
      </div>

      {collision && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" />
          {t('admin.moderation.overridesDuplicate', { node: collision })}
        </p>
      )}

      {entries.length === 0 ? (
        <p className="adm-text-muted">{t('admin.moderation.overridesEmpty')}</p>
      ) : (
        <ul className="adm-override-list">
          {entries.map(([id, enabled]) => (
            <li key={id} className="adm-override-item">
              <span className="adm-override-node">{nameOf(id)}</span>
              <span className={`adm-override-state ${enabled ? '' : 'adm-override-state--off'}`}>
                {enabled ? t('admin.moderation.overridesAllow') : t('admin.moderation.overridesBlock')}
              </span>
              <button type="button" className="btn-icon btn-icon--outline danger"
                title={t('common.remove')} aria-label={`${t('common.remove')} ${id}`}
                onClick={() => remove(id)}>
                <i className="fas fa-trash" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── 3. Bandeja de reportes ────────────────────────────────────────────────────

interface Filters {
  search: string;
  estado: UiStatus | '';
  ou: string;
  bu: string;
  site: string;
  desde: string;
  hasta: string;
  tipo: ReportRecoType | '';
}

const EMPTY_FILTERS: Filters = { search: '', estado: 'pendiente', ou: '', bu: '', site: '', desde: '', hasta: '', tipo: '' };

/**
 * Deep-link del aviso de moderación al admin (NotificationService → `?caso={recognitionId}`). Abre el
 * detalle de ESE caso en la bandeja. El valor es el id del RECONOCIMIENTO reportado (`reconocimientoId`),
 * no el del reporte.
 */
const CASO_PARAM = 'caso';

function ReportsInbox() {
  const { t, i18n } = useTranslation('recognition');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [detailOf, setDetailOf] = useState<ReportDto | null>(null);
  // RF-090 — el ✉️ manda un correo a una persona real: se confirma antes, como el resto de acciones
  // irreversibles de esta pantalla.
  const [escalating, setEscalating] = useState<ReportDto | null>(null);
  // Acuse por FILA, no en el modal: `isSuccess` de la mutación es compartido y quedaba pegado, así
  // que el segundo caso abría el modal ya en estado "enviado" y sin botones.
  const [notified, setNotifiedSet] = useState<ReadonlySet<string>>(new Set());
  const setNotified = (id: string) => setNotifiedSet((prev) => new Set(prev).add(id));

  // El estado se filtra en el servidor (es el único parámetro que el endpoint acepta); el resto
  // se resuelve en cliente sobre la bandeja completa, que es una cola de moderación, no un feed.
  // Cuanto necesitan las 8 columnas y los 6 filtros viene ya en el propio `ReportDto`.
  const reports = useModerationReports(filters.estado ? UI_TO_BACKEND[filters.estado] : undefined);
  const hide = useHideRecognition();
  const restore = useRestoreRecognition();
  const reopen = useReopenReport();
  const { data: ouItems = [] } = useOUItems();

  const list = useMemo(() => reports.data ?? [], [reports.data]);

  const setFilter = (p: Partial<Filters>) => { setFilters((f) => ({ ...f, ...p })); setPage(1); };

  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link ?caso={recognitionId}: abre el detalle de ese caso. La cola filtra "pendiente" por
  // defecto; si el caso enlazado no está en el filtro actual (regla del usuario: validar el estado),
  // se abre la bandeja completa y se reintenta antes de rendirse. El parámetro se limpia (replace) al
  // resolverlo para no reabrir el modal al navegar hacia atrás.
  useEffect(() => {
    const caso = searchParams.get(CASO_PARAM);
    if (!caso) return;
    if (reports.isLoading) return; // esperar la cola antes de buscar
    const match = list.find((r) => r.reconocimientoId === caso);
    if (match) {
      setDetailOf(match);
    } else if (filters.estado !== '') {
      // No está en el estado filtrado (p. ej. ya resuelto): ampliar a toda la bandeja y reintentar.
      setFilter({ estado: '' });
      return; // aún no limpiar el param: el efecto vuelve a correr con la lista completa
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(CASO_PARAM);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, reports.isLoading, list, filters.estado]);

  // Opciones de BU/Site: solo aparecen al elegir OU — el maquetado las mantiene ocultas
  // (`display:none`) hasta poblarlas.
  const inOu = useMemo(
    () => (r: ReportDto) => !filters.ou || r.ouId === filters.ou,
    [filters.ou],
  );
  const buOptions = useMemo(
    () => [...new Set(list.filter(inOu).map((r) => r.buName).filter((b): b is string => Boolean(b)))],
    [list, inOu],
  );
  const siteOptions = useMemo(
    () => [...new Set(list.filter(inOu).map((r) => r.siteName).filter((s): s is string => Boolean(s)))],
    [list, inOu],
  );

  const filtered = useMemo(() => list.filter((r) => {
    if (filters.search) {
      const hay = [r.reportante.nombre, r.reportado.nombre, recipientNames(r)]
        .join(' ').toLowerCase();
      if (!hay.includes(filters.search.trim().toLowerCase())) return false;
    }
    if (filters.ou && r.ouId !== filters.ou) return false;
    if (filters.bu && r.buName !== filters.bu) return false;
    if (filters.site && r.siteName !== filters.site) return false;
    if (filters.tipo && r.tipo !== filters.tipo) return false;
    if (filters.desde && r.fecha.slice(0, 10) < filters.desde) return false;
    if (filters.hasta && r.fecha.slice(0, 10) > filters.hasta) return false;
    return true;
  }), [list, filters]);

  const size = pageSize === 0 ? Math.max(filtered.length, 1) : pageSize;
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const safePage = Math.min(page, pages);
  const slice = filtered.slice((safePage - 1) * size, safePage * size);

  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { day: '2-digit', month: 'short', year: 'numeric' });
  const actionError = hide.error ?? restore.error ?? reopen.error;

  return (
    <div className="adm-inbox">
      <h4 className="adm-sub-title">{t('admin.moderation.inboxTitle')}</h4>
      <p className="adm-block-desc">{t('admin.moderation.inboxDesc')}</p>

      <div className="adm-filter-bar">
        <div className="adm-filter-search">
          <i className="fas fa-magnifying-glass" />
          <input type="text" value={filters.search} placeholder={t('admin.moderation.searchName')}
            aria-label={t('admin.moderation.searchName')}
            onChange={(e) => setFilter({ search: e.target.value })} />
        </div>

        <select className="adm-filter-select" value={filters.estado} aria-label={t('admin.moderation.colStatus')}
          onChange={(e) => setFilter({ estado: e.target.value as UiStatus | '' })}>
          <option value="pendiente">{t('admin.moderation.uiStatusPlural.pendiente')}</option>
          <option value="restaurado">{t('admin.moderation.uiStatusPlural.restaurado')}</option>
          <option value="eliminado">{t('admin.moderation.uiStatusPlural.eliminado')}</option>
          <option value="">{t('admin.moderation.filterAll')}</option>
        </select>

        <select className="adm-filter-select" value={filters.ou} aria-label={t('admin.moderation.filterOu')}
          onChange={(e) => setFilter({ ou: e.target.value, bu: '', site: '' })}>
          <option value="">{t('admin.moderation.filterOu')}</option>
          {ouItems.map((ou) => <option key={ou.id} value={ou.id}>{ou.name}</option>)}
        </select>

        {filters.ou && buOptions.length > 0 && (
          <select className="adm-filter-select" value={filters.bu} aria-label={t('admin.moderation.filterBu')}
            onChange={(e) => setFilter({ bu: e.target.value, site: '' })}>
            <option value="">{t('admin.moderation.filterBu')}</option>
            {buOptions.map((bu) => <option key={bu} value={bu}>{bu}</option>)}
          </select>
        )}
        {filters.ou && siteOptions.length > 0 && (
          <select className="adm-filter-select" value={filters.site} aria-label={t('admin.moderation.filterSite')}
            onChange={(e) => setFilter({ site: e.target.value })}>
            <option value="">{t('admin.moderation.filterSite')}</option>
            {siteOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        <input type="date" className="adm-filter-date" value={filters.desde} aria-label={t('admin.moderation.filterFrom')}
          onChange={(e) => setFilter({ desde: e.target.value })} />
        <span className="adm-filter-date-sep">—</span>
        <input type="date" className="adm-filter-date" value={filters.hasta} aria-label={t('admin.moderation.filterTo')}
          onChange={(e) => setFilter({ hasta: e.target.value })} />

        <select className="adm-filter-select" value={filters.tipo} aria-label={t('admin.moderation.filterType')}
          onChange={(e) => setFilter({ tipo: e.target.value as ReportRecoType | '' })}>
          <option value="">{t('admin.moderation.filterType')}</option>
          <option value="fundamentales">{t('admin.moderation.type.fundamentales')}</option>
          <option value="brillamos">{t('admin.moderation.type.brillamos')}</option>
          <option value="diario">{t('admin.moderation.type.diario')}</option>
        </select>
      </div>

      {actionError ? (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(actionError, t('admin.saveError'))}
        </p>
      ) : null}

      <StateBlock
        isLoading={reports.isLoading}
        isError={reports.isError}
        error={reports.error}
        isEmpty={filtered.length === 0}
        emptyText={t('admin.moderation.reportsEmpty')}
        onRetry={() => { reports.refetch(); }}
      >
        <div className="adm-table-wrap">
          <table className="adm-table adm-table--cards">
            <thead>
              <tr>
                <th>{t('admin.moderation.colReportedBy')}</th>
                <th>{t('admin.moderation.colRecognitionFrom')}</th>
                <th>{t('admin.moderation.colTo')}</th>
                <th>{t('admin.moderation.colDetail')}</th>
                <th>{t('admin.moderation.colReason')}</th>
                <th>{t('admin.moderation.colDate')}</th>
                <th>{t('admin.moderation.colStatus')}</th>
                <th>{t('admin.moderation.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {slice.map((r) => {
                const ui = toUiStatus(r.estado);
                return (
                  <tr key={r.id}>
                    <td data-label={t('admin.moderation.colReportedBy')}>{r.reportante.nombre}</td>
                    <td data-label={t('admin.moderation.colRecognitionFrom')}>{r.reportado.nombre}</td>
                    <td data-label={t('admin.moderation.colTo')}>
                      {recipientNames(r) || <span className="adm-text-muted">—</span>}
                    </td>
                    <td data-label={t('admin.moderation.colDetail')} className="adm-cell-clamp">
                      {r.mensajePreview || <span className="adm-text-muted">—</span>}
                    </td>
                    <td data-label={t('admin.moderation.colReason')} className="adm-cell-clamp">“{r.motivo}”</td>
                    <td data-label={t('admin.moderation.colDate')}>{fmtDate(r.fecha)}</td>
                    <td data-label={t('admin.moderation.colStatus')}><StatusPill estado={r.estado} /></td>
                    <td data-label={t('admin.moderation.colActions')}>
                      <div className="adm-actions">
                        {ui === 'restaurado' ? (
                          <button type="button" className="btn btn--ghost" disabled={reopen.isPending}
                            title={t('admin.moderation.reopenHint')}
                            onClick={() => reopen.mutate(r.reconocimientoId)}>
                            <i className="fas fa-rotate-right" /> {t('admin.moderation.reopen')}
                          </button>
                        ) : (
                          <>
                            <button type="button" className="btn btn--danger" disabled={hide.isPending}
                              title={t('admin.moderation.hideHint')}
                              onClick={() => hide.mutate(r.reconocimientoId)}>
                              <i className="fas fa-eye-slash" /> {t('admin.moderation.deleteLabel')}
                            </button>
                            <button type="button" className="adm-btn-ok" disabled={restore.isPending}
                              onClick={() => restore.mutate(r.reconocimientoId)}>
                              <i className="fas fa-check" /> {t('admin.moderation.restore')}
                            </button>
                          </>
                        )}
                        {/* RF-090 — escala el caso al admin de la OU/país AHORA, sin esperar al
                            digest semanal. No cambia el estado del reporte, así que no altera el
                            orden FIFO de la bandeja (RF-089). Abre el modal: el envío y su
                            pre-validación viven allí, con los destinatarios a la vista. */}
                        <button type="button"
                          className={`adm-icon-btn ${notified.has(r.id) ? 'adm-icon-btn--done' : ''}`}
                          title={notified.has(r.id)
                            ? t('admin.moderation.notifyAdminSent')
                            : t('admin.moderation.notifyAdminHint')}
                          aria-label={`${t('admin.moderation.notifyAdmin')} ${r.reportado.nombre}`}
                          onClick={() => setEscalating(r)}>
                          <i className={notified.has(r.id) ? 'fas fa-envelope-circle-check' : 'fas fa-envelope'} />
                        </button>
                        <button type="button" className="btn-icon btn-icon--outline" title={t('admin.moderation.viewDetail')}
                          aria-label={t('admin.moderation.viewDetail')} onClick={() => setDetailOf(r)}>
                          <i className="fas fa-magnifying-glass" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <AdminPagerFooter total={filtered.length} page={safePage} pageSize={pageSize}
          onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
      </StateBlock>

      <ReportDetailModal report={detailOf} onClose={() => setDetailOf(null)} />

      {/* RF-090 — confirmación del ✉️: el envío es a una persona real y no se puede deshacer. */}
      {escalating && (
        <EscalateModal
          report={escalating}
          onClose={() => setEscalating(null)}
          onSent={() => { setNotified(escalating.id); setEscalating(null); }}
        />
      )}
    </div>
  );
}

/**
 * RF-090 — modal del ✉️. Vive en su propio componente porque necesita CONSULTAR quién recibirá el
 * aviso, y un hook no puede colgar de un `escalating &&` dentro de la bandeja.
 *
 * El defecto que corrige: el POST devolvía `success: true` en cuanto el evento entraba al bus, y
 * cuanto podía salir mal —OU sin administradores, admin sin correo, directorio caído, lista blanca de
 * destinatarios— ocurría después, en NotificationService, dejando solo un warning en el log. El
 * moderador veía el icono en verde y daba el caso por escalado sin que lo recibiera nadie.
 *
 * Por eso: el botón de enviar solo se habilita si la previsualización resolvió AL MENOS un
 * destinatario entregable, y cuando no, se explica el motivo en vez de dejarlo confirmar a ciegas.
 */
function EscalateModal({ report, onClose, onSent }: Readonly<{
  report: ReportDto;
  onClose: () => void;
  onSent: () => void;
}>) {
  const { t } = useTranslation('recognition');
  const preview = useModerationNotifyPreview(report.id);
  const notifyAdmin = useNotifyModerationAdmin();

  const plan = preview.data;
  const canSend = Boolean(plan?.canNotify) && !notifyAdmin.isPending;

  // El 422 del POST trae el CÓDIGO del motivo, no un texto: se traduce igual que el de la
  // previsualización en vez de rendir el mensaje del backend, que viene solo en español.
  const sendErrorText = notifyAdmin.isError
    ? reasonText(t, getRecognitionErrorReason(notifyAdmin.error))
      ?? getRecognitionErrorMessage(notifyAdmin.error, t('admin.moderation.notifyAdminError'))
    : null;

  return (
    <Modal open onClose={onClose} maxWidth={480} labelledBy={ESCALATE_TITLE_ID}>
      <ModalHeader onClose={onClose}>
        <h2 id={ESCALATE_TITLE_ID}>{t('admin.moderation.notifyAdmin')}</h2>
      </ModalHeader>
      <ModalBody>
        <p>{t('admin.moderation.notifyAdminConfirm', { name: report.reportado.nombre })}</p>

        {preview.isLoading && (
          <p className="adm-text-muted">{t('admin.moderation.notifyPreviewLoading')}</p>
        )}

        {preview.isError && (
          <p className="adm-error" role="alert">
            <i className="fas fa-triangle-exclamation" />{' '}
            {getRecognitionErrorMessage(preview.error, t('admin.moderation.notifyPreviewError'))}
          </p>
        )}

        {plan && (
          <>
            <p className="adm-block-desc">{t('admin.moderation.notifyPreviewTitle')}</p>

            {plan.recipients.length === 0 ? (
              <p className="adm-text-muted">{t('admin.moderation.notifyPreviewEmpty')}</p>
            ) : (
              <ul className="adm-notify-list">
                {plan.recipients.map((r) => (
                  <li key={r.userId}
                    className={`adm-notify-item ${r.deliverable ? '' : 'adm-notify-item--off'}`}>
                    <span className="adm-notify-name">
                      {r.name || t('admin.moderation.dataUnavailable')}
                    </span>
                    <span className="adm-notify-mail">
                      {r.email || t('admin.moderation.dataUnavailable')}
                    </span>
                    {!r.deliverable && (
                      <span className="adm-notify-blocked">{reasonText(t, r.blocked)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {!plan.canNotify && (
              <p className="adm-error" role="alert">
                <i className="fas fa-triangle-exclamation" /> {reasonText(t, plan.reason)}
              </p>
            )}

            {/* Avisos que NO impiden enviar, pero que el moderador debe conocer antes de confirmar:
                el escalado a globales y, en especial, la lista blanca, cuya supresión no deja rastro
                en ningún log de notificación. Se omiten los que YA están explicados junto a la
                persona a la que afectan: repetir el mismo texto dos veces solo lo vuelve ruido. */}
            {(plan.warnings ?? [])
              .filter((w) => w !== plan.reason && !plan.recipients.some((r) => r.blocked === w))
              .map((w) => (
                <p key={w} className="adm-notify-warn">
                  <i className="fas fa-circle-info" /> {reasonText(t, w)}
                </p>
              ))}
          </>
        )}

        {sendErrorText && (
          <p className="adm-error" role="alert">
            <i className="fas fa-triangle-exclamation" /> {sendErrorText}
          </p>
        )}

        <div className="rec-edit-actions">
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn--primary" disabled={!canSend}
            onClick={() => notifyAdmin.mutate(report.id, { onSuccess: onSent })}>
            <i className="fas fa-envelope" /> {t('admin.moderation.notifyAdmin')}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}

/** Traduce un código de motivo de RF-090. `undefined`/`null` → sin texto. */
function reasonText(
  t: TFunction<'recognition'>,
  reason: string | null | undefined,
): string | undefined {
  if (!reason) return undefined;
  return t(`admin.moderation.notifyReason.${reason}`, {
    defaultValue: t('admin.moderation.notifyReason.desconocido'),
  });
}

const STATUS_BADGE: Record<UiStatus, string> = {
  pendiente: 'adm-badge--warn',
  restaurado: 'adm-badge--on',
  eliminado: 'adm-badge--danger',
};

function StatusPill({ estado }: Readonly<{ estado: ModerationStatus }>) {
  const { t } = useTranslation('recognition');
  const ui = toUiStatus(estado);
  if (!ui) return <span className="adm-badge">{t(`admin.moderation.status.${estado}`)}</span>;
  return <span className={`adm-badge ${STATUS_BADGE[ui]}`}>{t(`admin.moderation.uiStatus.${ui}`)}</span>;
}

// ─── Modal de detalle del reporte ──────────────────────────────────────────────

const DETAIL_TITLE_ID = 'adm-report-detail-title';

/**
 * La fila lleva un RESUMEN del mensaje; aquí hace falta el texto íntegro, así que el modal pide
 * `GET v1/moderation/reports/{id}` — una sola petición, y solo al abrir el caso. Mientras llega (o si
 * falla) se pinta con lo que la fila ya tenía: se ve el caso completo salvo el texto largo.
 *
 * RF-087 — las tarjetas de persona se pintan con el DETALLE, no con la fila. `ReportDto` lleva el
 * SNAPSHOT congelado en el instante del reporte (`ReportRecognitionCommandHandler` → `Party`), y ese
 * snapshot nace vacío siempre que UserAdminService no respondió en ese momento: `UserProfileClient`
 * degrada a `new UserContact(userId, userId, null, null, null, false)`, o sea nombre = GUID y correo
 * y OU en null. Los reportes anteriores a WP tampoco los tienen. `GET v1/moderation/reports/{id}`
 * resuelve correo/OU EN VIVO (`GetReportByIdQueryHandler.EnrichAsync`) y conserva lo almacenado solo
 * como respaldo, así que es la única fuente con la que el moderador puede escalar el caso.
 */
function ReportDetailModal({ report, onClose }: Readonly<{
  report: ReportDto | null;
  onClose: () => void;
}>) {
  const { t, i18n } = useTranslation('recognition');
  const detailQuery = useModerationReportDetail(report?.id);
  if (!report) return null;

  const detail: ReportDetailDto | undefined = detailQuery.data;
  const receptores = recipientNames(detail ?? report);
  const fecha = new Date(report.fecha).toLocaleDateString(i18n.language, { day: '2-digit', month: 'short', year: 'numeric' });

  // Fila como andamio mientras llega el detalle (o si falla): el caso se ve igual, con el dato
  // congelado en vez del vivo. Nunca al revés — el detalle manda en cuanto está.
  const reportante = detail?.reportante ?? report.reportante;
  const reportado = detail?.reportado ?? report.reportado;

  return (
    <Modal open onClose={onClose} maxWidth={640} labelledBy={DETAIL_TITLE_ID}>
      <ModalHeader onClose={onClose}><h2 id={DETAIL_TITLE_ID}>{t('admin.moderation.detailTitle')}</h2></ModalHeader>
      <ModalBody>
        {/* Si el detalle no carga, el modal se queda con el snapshot: hay que decirlo, porque un
            correo ausente por fallo de red es indistinguible de un correo que nunca existió. */}
        {detailQuery.isError && (
          <p className="adm-error" role="alert">
            <i className="fas fa-triangle-exclamation" />{' '}
            {getRecognitionErrorMessage(detailQuery.error, t('admin.moderation.detailError'))}
          </p>
        )}

        <div className="adm-person-cards">
          <PersonCard label={t('admin.moderation.whoReported')} tone="purple"
            name={reportante.nombre} email={reportante.correo} ou={reportante.ouName}
            pending={detailQuery.isLoading} />
          <PersonCard label={t('admin.moderation.whoWasReported')} tone="red"
            name={reportado.nombre} email={reportado.correo} ou={reportado.ouName}
            bu={detail?.buName ?? report.buName} site={detail?.siteName ?? report.siteName}
            pending={detailQuery.isLoading} />
        </div>

        <div className="adm-detail-block">
          <div className="adm-detail-head">{t('admin.moderation.reportedRecognition')}</div>
          <div className="adm-detail-to">
            <strong>{t('admin.moderation.colTo')}:</strong>{' '}
            {receptores || <span className="adm-text-muted">—</span>}
          </div>
          <blockquote className="adm-quote">
            {detail?.mensaje ?? report.mensajePreview
              ?? <span className="adm-text-muted">{t('admin.moderation.messageUnavailable')}</span>}
          </blockquote>
          <div className="adm-detail-meta">
            <span><strong>{t('admin.moderation.colReason')}:</strong> {report.motivo}</span>
            <span><strong>{t('admin.moderation.colDate')}:</strong> {fecha}</span>
            <span><strong>{t('admin.moderation.colStatus')}:</strong> <StatusPill estado={report.estado} /></span>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}

/**
 * RF-087 — correo y OU son el instrumento con el que el moderador escala el caso al admin del país
 * correcto, así que sus nodos se pintan SIEMPRE: antes se omitían cuando el valor venía vacío y la
 * tarjeta quedaba reducida a un nombre —o a un GUID—, que es exactamente lo que QA reportó como «no
 * existe esa funcionalidad». Un hueco silencioso no se distingue de una función sin construir.
 *
 * BU y Site siguen siendo condicionales: RF-087 no los pide (nota de alcance de `decisiones.md`) y
 * hoy solo los tiene el personal dado de alta por Frontline/Europa — la sincronización con Entra no
 * escribe `BuId`/`SiteId`, así que un marcador fijo sería ruido en la inmensa mayoría de los casos.
 *
 * Los prefijos «OU:/BU:/Site:» son los del maquetado (`_reportPersonCard`): sin ellos un marcador de
 * dato ausente sería un chip suelto que no dice de qué campo habla.
 */
function PersonCard({ label, tone, name, email, ou, bu, site, pending }: Readonly<{
  label: string; tone: 'purple' | 'red';
  name: string; email?: string | null; ou?: string | null; bu?: string | null; site?: string | null;
  /** El detalle en vuelo: el hueco es provisional, no una ausencia confirmada. */
  pending?: boolean;
}>) {
  const { t } = useTranslation('recognition');
  const missing = pending ? t('admin.moderation.detailLoading') : t('admin.moderation.dataUnavailable');

  return (
    <div className="adm-person-card">
      <div className={`adm-person-label adm-person-label--${tone}`}>{label}</div>
      <div className="adm-person-name">{name}</div>
      <div className="adm-person-email">
        {email || <span className="adm-text-muted">{missing}</span>}
      </div>
      <div className="adm-person-chips">
        <span className="adm-person-chip">{t('admin.moderation.chipOu')}: {ou || missing}</span>
        {bu && <span className="adm-person-chip adm-person-chip--bu">{t('admin.moderation.chipBu')}: {bu}</span>}
        {site && <span className="adm-person-chip adm-person-chip--site">{t('admin.moderation.chipSite')}: {site}</span>}
      </div>
    </div>
  );
}

// ─── Subvista: gestor de palabras bloqueadas ───────────────────────────────────

function BlockedWordsManager({ onBack }: Readonly<{ onBack: () => void }>) {
  const { t } = useTranslation('recognition');
  const words = useBlockedWords();
  const localeMap = useCountryLanguages();
  const createM = useCreateBlockedWord();
  const updateM = useUpdateBlockedWord();
  const deleteM = useDeleteBlockedWord();

  const [newWord, setNewWord] = useState('');
  const [newLang, setNewLang] = useState('');
  const [newRegion, setNewRegion] = useState<string>(ANY_COUNTRY);
  const [tab, setTab] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);

  const list = useMemo(() => words.data ?? [], [words.data]);
  const langs = useMemo(() => languageTabs(list), [list]);
  const countries = useMemo(() => countryOptions(localeMap.data, list), [localeMap.data, list]);

  // El alta solo puede nacer en uno de los 7; el backend rechaza el resto.
  const selectedLang = newLang || SUPPORTED_LANGUAGE_CODES[0];

  // El conteo de la pestaña es AGREGADO por idioma base: una palabra legacy `es-MX` cuenta en `es`.
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    langs.forEach((tabId) => map.set(tabId, list.filter((w) => inLanguageTab(w, tabId)).length));
    return map;
  }, [list, langs]);

  const filtered = useMemo(() => list.filter((w) => {
    if (tab !== 'all' && !inLanguageTab(w, tab)) return false;
    if (region !== 'all' && w.country !== region) return false;
    if (search && !w.word.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  }), [list, tab, region, search]);

  const size = pageSize === 0 ? Math.max(filtered.length, 1) : pageSize;
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const safePage = Math.min(page, pages);
  const slice = filtered.slice((safePage - 1) * size, safePage * size);

  const add = () => {
    if (!newWord.trim() || !selectedLang) return;
    createM.mutate(
      { word: newWord.trim(), country: newRegion, languageCode: selectedLang },
      { onSuccess: () => setNewWord('') },
    );
  };

  const saveEdit = (w: BlockedWordDto) => {
    if (!editing || !editing.text.trim() || editing.text.trim() === w.word) { setEditing(null); return; }
    updateM.mutate(
      { id: w.id, body: { word: editing.text.trim(), country: w.country, languageCode: w.languageCode } },
      { onSuccess: () => setEditing(null) },
    );
  };

  const mutationError = createM.error ?? updateM.error ?? deleteM.error;

  return (
    <div className="adm-words-view">
      <div className="adm-words-head">
        <button type="button" className="adm-back-btn" onClick={onBack}>← {t('admin.moderation.title')}</button>
        <div>
          <h3 className="adm-words-title">{t('admin.moderation.wordsTitle')}</h3>
          <p className="adm-words-subtitle">{t('admin.moderation.wordsManagerDesc')}</p>
        </div>
      </div>

      <CountryLanguagesEditor />

      <div className="adm-words-form">
        <label className="adm-words-field">
          <span>{t('admin.moderation.language')}</span>
          <select className="field" value={selectedLang} onChange={(e) => setNewLang(e.target.value)}>
            {SUPPORTED_LANGUAGE_CODES.map((l) => (
              <option key={l} value={l}>{l.toUpperCase()}</option>
            ))}
          </select>
        </label>
        <label className="adm-words-field adm-words-field--grow">
          <span>{t('admin.moderation.newWord')}</span>
          <input type="text" value={newWord} placeholder={t('admin.moderation.wordPlaceholder')}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
        </label>
        <label className="adm-words-field">
          <span>{t('admin.moderation.region')}</span>
          <select className="field" value={newRegion} onChange={(e) => setNewRegion(e.target.value)}>
            <option value={ANY_COUNTRY}>{t('admin.moderation.regionGlobal')}</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <button type="button" className="btn btn--primary"
          disabled={!newWord.trim() || !selectedLang || createM.isPending} onClick={add}>
          <i className="fas fa-plus" /> {t('admin.moderation.addWord')}
        </button>
      </div>

      {mutationError ? (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(mutationError, t('admin.saveError'))}
        </p>
      ) : null}

      <div className="adm-words-filters">
        <div className="adm-lang-tabs" role="tablist" aria-label={t('admin.moderation.language')}>
          <button type="button" role="tab" aria-selected={tab === 'all'}
            className={`adm-lang-tab ${tab === 'all' ? 'adm-lang-tab--on' : ''}`}
            onClick={() => { setTab('all'); setPage(1); }}>
            {t('admin.moderation.filterAll')} <span className="adm-lang-count">{list.length}</span>
          </button>
          {langs.map((l) => (
            <button key={l} type="button" role="tab" aria-selected={tab === l}
              className={`adm-lang-tab ${tab === l ? 'adm-lang-tab--on' : ''}`}
              onClick={() => { setTab(l); setPage(1); }}>
              {l === OTHER_LANG ? t('admin.moderation.langOther') : l.toUpperCase()}{' '}
              <span className="adm-lang-count">{counts.get(l) ?? 0}</span>
            </button>
          ))}
        </div>
        <input type="search" className="adm-words-search" value={search} placeholder={t('admin.moderation.searchWord')}
          aria-label={t('admin.moderation.searchWord')}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <select className="adm-filter-select" value={region} aria-label={t('admin.moderation.region')}
          title={t('admin.moderation.regionHint')}
          onChange={(e) => { setRegion(e.target.value); setPage(1); }}>
          <option value="all">{t('admin.moderation.allRegions')}</option>
          <option value={ANY_COUNTRY}>{t('admin.moderation.regionGlobal')}</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <StateBlock
        isLoading={words.isLoading}
        isError={words.isError}
        error={words.error}
        isEmpty={filtered.length === 0}
        emptyText={t('admin.moderation.wordsEmpty')}
        onRetry={() => { words.refetch(); }}
      >
        <div className="adm-table-wrap">
          <table className="adm-table adm-table--glass">
            <thead>
              <tr>
                <th style={{ width: '40%' }}>{t('admin.moderation.word')}</th>
                <th>{t('admin.moderation.language')}</th>
                <th>{t('admin.moderation.region')}</th>
                <th>{t('admin.moderation.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {slice.map((w) => {
                const isEditing = editing?.id === w.id;
                return (
                  <tr key={w.id}>
                    <td>
                      {isEditing ? (
                        <input type="text" className="adm-word-input" value={editing.text} autoFocus
                          aria-label={t('admin.moderation.word')}
                          onChange={(e) => setEditing({ id: w.id, text: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit(w);
                            else if (e.key === 'Escape') setEditing(null);
                          }} />
                      ) : w.word}
                    </td>
                    <td>
                      <span className={`adm-lang-badge adm-lang-badge--${w.languageCode.toLowerCase()}`}>
                        {w.languageCode}
                      </span>
                    </td>
                    <td>{w.country === ANY_COUNTRY ? t('admin.moderation.regionGlobal') : w.country}</td>
                    <td>
                      <div className="adm-actions">
                        {isEditing ? (
                          <button type="button" className="adm-btn-ok" disabled={updateM.isPending}
                            title={t('admin.save')} aria-label={t('admin.save')} onClick={() => saveEdit(w)}>
                            <i className="fas fa-check" />
                          </button>
                        ) : (
                          <button type="button" className="btn-icon btn-icon--outline" title={t('admin.moderation.editWord')}
                            aria-label={t('admin.moderation.editWord')} onClick={() => setEditing({ id: w.id, text: w.word })}>
                            <i className="fas fa-pen" />
                          </button>
                        )}
                        <button type="button" className="btn-icon btn-icon--outline danger" disabled={deleteM.isPending}
                          title={t('common.remove')} aria-label={t('common.remove')} onClick={() => deleteM.mutate(w.id)}>
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <AdminPagerFooter total={filtered.length} page={safePage} pageSize={pageSize}
          onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
      </StateBlock>
    </div>
  );
}

// ─── Subvista: tabla país → idiomas (RF-082) ───────────────────────────────────

/**
 * Editor de la tabla que gobierna qué catálogo aplica a cada emisor. Va plegado porque es
 * configuración de fondo —se toca cuando entra un país nuevo, no a diario— pero tiene que estar
 * junto al gestor de palabras: es ahí donde el administrador descubre que Bélgica necesita
 * neerlandés y francés, y cambiarla sin ver el catálogo sería a ciegas.
 *
 * Guardarla exige alcance global; para el resto de moderadores el PUT responde 403 y el error se
 * muestra tal cual, en vez de esconder los controles y dejar la pantalla sin explicación.
 */
function CountryLanguagesEditor() {
  const { t } = useTranslation('recognition');
  const query = useCountryLanguages();
  const save = useSaveCountryLanguages();

  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [regionDraft, setRegionDraft] = useState<Record<string, string> | null>(null);
  const [newCountry, setNewCountry] = useState('');

  // Se edita como texto ("es-MX, fr-FR") y solo al guardar se parte en lista: obligar a manejar
  // arreglos por tecla haría imposible escribir una coma.
  const rows = draft ?? Object.fromEntries(
    Object.entries(query.data?.countries ?? {}).map(([c, langs]) => [c, langs.join(', ')]));

  const patch = (next: Record<string, string>) => setDraft(next);

  // Códigos que el backend rechazaría o normalizaría a otra cosa: se avisan ANTES de guardar, en vez
  // de dejar que el PUT falle entero o, peor, que se guarde algo distinto de lo que se tecleó.
  const invalid = Object.entries(rows)
    .filter(([code, value]) => !isIsoCountry(code) || !value.split(',').every(isLanguageTagOrEmpty))
    .map(([code]) => code);

  const addCountry = () => {
    const code = newCountry.trim().toUpperCase();
    if (!isIsoCountry(code) || rows[code] !== undefined) return;
    patch({ ...rows, [code]: '' });
    setNewCountry('');
  };

  const removeCountry = (code: string) => {
    const next = { ...rows };
    delete next[code];
    patch(next);
  };

  // Las regiones (@LATAM/@EU) también son editables: agrupan países y son las que hacen que una
  // palabra dada de alta como "Europa" siga aplicando. Antes se enviaban tal cual llegaban… salvo
  // que el GET hubiera fallado, en cuyo caso se vaciaban.
  const regionRows = regionDraft ?? Object.fromEntries(
    Object.entries(query.data?.regions ?? {}).map(([r, members]) => [r, members.join(', ')]));

  const commitRegions = (next: Record<string, string>) => setRegionDraft(next);

  const commit = () => {
    const countries: Record<string, string[]> = {};
    Object.entries(rows).forEach(([code, value]) => {
      const langs = value.split(',').map((l) => l.trim()).filter(Boolean);
      if (langs.length > 0) countries[code] = langs;
    });
    // Las regiones se editan como texto ("MX, CO") igual que los idiomas; se parten al guardar.
    const regions: Record<string, string[]> = {};
    Object.entries(regionRows).forEach(([region, value]) => {
      const members = value.split(',').map((c) => c.trim().toUpperCase()).filter(Boolean);
      if (members.length > 0) regions[region] = members;
    });

    save.mutate(
      { countries, regions },
      { onSuccess: () => { setDraft(null); setRegionDraft(null); } },
    );
  };

  // El PUT REEMPLAZA la tabla entera. Guardar sin haber podido leerla la sustituiría por lo poco que
  // haya en pantalla y borraría el resto — con el GET caído no se guarda, y se dice por qué.
  const canSave = Boolean(query.data) && !query.isError && invalid.length === 0;

  return (
    <details className="adm-advanced adm-country-langs">
      <summary>{t('admin.moderation.countryLanguagesTitle')}</summary>
      <p className="adm-block-desc">{t('admin.moderation.countryLanguagesDesc')}</p>

      <StateBlock
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={Object.keys(rows).length === 0}
        emptyText={t('admin.moderation.countryLanguagesEmpty')}
        onRetry={() => { query.refetch(); }}
      >
        <div className="adm-table-wrap">
          <table className="adm-table adm-table--glass">
            <thead>
              <tr>
                <th style={{ width: '20%' }}>{t('admin.moderation.country')}</th>
                <th>{t('admin.moderation.languages')}</th>
                <th>{t('admin.moderation.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(rows).sort(([a], [b]) => a.localeCompare(b)).map(([code, value]) => (
                <tr key={code}>
                  <td>{code}</td>
                  <td>
                    <input type="text" className="adm-word-input" value={value}
                      aria-label={`${t('admin.moderation.languages')} ${code}`}
                      placeholder={t('admin.moderation.languagesPlaceholder')}
                      onChange={(e) => patch({ ...rows, [code]: e.target.value })} />
                  </td>
                  <td>
                    <button type="button" className="btn-icon btn-icon--outline danger"
                      title={t('common.remove')} aria-label={`${t('common.remove')} ${code}`}
                      onClick={() => removeCountry(code)}>
                      <i className="fas fa-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </StateBlock>

      <div className="adm-words-form">
        <label className="adm-words-field">
          <span>{t('admin.moderation.addCountry')}</span>
          <input type="text" value={newCountry} maxLength={2}
            placeholder={t('admin.moderation.countryPlaceholder')}
            onChange={(e) => setNewCountry(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCountry(); }} />
        </label>
        <button type="button" className="btn btn--primary"
          disabled={!isIsoCountry(newCountry)} onClick={addCountry}>
          <i className="fas fa-plus" /> {t('admin.moderation.addCountry')}
        </button>
      </div>

      {/* Regiones: agrupan países y son las que mantienen vivas las palabras dadas de alta como
          "Europa"/"LATAM". Sin este editor, el PUT las enviaba vacías si el GET había fallado. */}
      <h5 className="adm-sub-title">{t('admin.moderation.regionsTitle')}</h5>
      <div className="adm-table-wrap">
        <table className="adm-table adm-table--glass">
          <thead>
            <tr>
              <th style={{ width: '20%' }}>{t('admin.moderation.region')}</th>
              <th>{t('admin.moderation.regionMembers')}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(regionRows).sort(([a], [b]) => a.localeCompare(b)).map(([region, members]) => (
              <tr key={region}>
                <td>{region}</td>
                <td>
                  <input type="text" className="adm-word-input" value={members}
                    aria-label={`${t('admin.moderation.regionMembers')} ${region}`}
                    placeholder="MX, CO, BR"
                    onChange={(e) => commitRegions({ ...regionRows, [region]: e.target.value })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {invalid.length > 0 && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" />{' '}
          {t('admin.moderation.countryLanguagesInvalid', { codes: invalid.join(', ') })}
        </p>
      )}

      {!query.data && !query.isLoading && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" /> {t('admin.moderation.countryLanguagesUnreadable')}
        </p>
      )}

      {save.isError && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(save.error, t('admin.saveError'))}
        </p>
      )}

      <div className="adm-save-row">
        <AdminSaveBar onSave={commit} saving={save.isPending}
          dirty={(draft !== null || regionDraft !== null) && canSave} saved={save.isSuccess} />
      </div>
    </details>
  );
}

export default AdminModeration;
